import React, { useState, useEffect } from 'react';
import { BlogPost, BlogCategory, BlogAuthor } from '@/types/blog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

interface BlogFormProps {
  initialValues?: Partial<BlogPost>;
  categories: BlogCategory[];
  authors: BlogAuthor[];
  loading?: boolean;
  onSubmit: (values: Partial<BlogPost>) => void;
}

const statusOptions = ["draft", "published", "archived"];

const BlogForm: React.FC<BlogFormProps> = ({ initialValues = {}, categories, authors, loading, onSubmit }) => {
  const [values, setValues] = useState<Partial<BlogPost>>({
    title: '',
    content: '',
    category: categories[0] || undefined,
    author: authors[0] || undefined,
    tags: [],
    status: 'draft',
    ...initialValues,
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    setValues(v => ({ ...v, ...initialValues }));
  }, [initialValues]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setValues(v => ({ ...v, [name]: value }));
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const cat = categories.find(c => c.id === e.target.value);
    setValues(v => ({ ...v, category: cat }));
  };

  const handleAuthorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const author = authors.find(a => a.id === e.target.value);
    setValues(v => ({ ...v, author }));
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setValues(v => ({ ...v, status: e.target.value }));
  };

  const handleAddTag = () => {
    if (tagInput && !values.tags?.includes(tagInput)) {
      setValues(v => ({ ...v, tags: [...(v.tags || []), tagInput] }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setValues(v => ({ ...v, tags: (v.tags || []).filter(t => t !== tag) }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(values);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8 max-w-2xl mx-auto" aria-label="Blog Post Form">
      {/* Blog Details Group */}
      <fieldset className="border rounded p-4" aria-labelledby="blog-details-legend">
        <legend id="blog-details-legend" className="font-bold mb-2">Blog Details</legend>
        <div className="mb-4">
          <label htmlFor="title" className="block font-medium mb-1">Title</label>
          <Input id="title" name="title" value={values.title || ''} onChange={handleChange} required disabled={loading} aria-required="true" aria-label="Blog Title" tabIndex={1} />
        </div>
        <div className="mb-4">
          <label htmlFor="excerpt" className="block font-medium mb-1">Excerpt</label>
          <Textarea id="excerpt" name="excerpt" value={values.excerpt || ''} onChange={handleChange} rows={2} disabled={loading} aria-label="Blog Excerpt" tabIndex={2} />
        </div>
        <div className="mb-4">
          <label htmlFor="content" className="block font-medium mb-1">Content</label>
          <Textarea id="content" name="content" value={values.content || ''} onChange={handleChange} rows={8} required disabled={loading} aria-required="true" aria-label="Blog Content" tabIndex={3} />
        </div>
      </fieldset>
      {/* Categorization Group */}
      <fieldset className="border rounded p-4" aria-labelledby="categorization-legend">
        <legend id="categorization-legend" className="font-bold mb-2">Categorization</legend>
        <div className="mb-4">
          <label htmlFor="category" className="block font-medium mb-1">Category</label>
          <select id="category" name="category" value={values.category?.id || ''} onChange={handleCategoryChange} className="w-full border rounded p-2" disabled={loading} aria-label="Category" tabIndex={4}>
            {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
          </select>
        </div>
        <div className="mb-4">
          <label htmlFor="author" className="block font-medium mb-1">Author</label>
          <select id="author" name="author" value={values.author?.id || ''} onChange={handleAuthorChange} className="w-full border rounded p-2" disabled={loading} aria-label="Author" tabIndex={5}>
            {authors.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div className="mb-4">
          <label htmlFor="status" className="block font-medium mb-1">Status</label>
          <select id="status" name="status" value={values.status || 'draft'} onChange={handleStatusChange} className="w-full border rounded p-2" disabled={loading} aria-label="Status" tabIndex={6}>
            {statusOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
      </fieldset>
      {/* Tags Group */}
      <fieldset className="border rounded p-4" aria-labelledby="tags-legend">
        <legend id="tags-legend" className="font-bold mb-2">Tags</legend>
        <div className="flex gap-2 mb-2">
          <label htmlFor="tag-input" className="sr-only">Add tag</label>
          <Input id="tag-input" value={tagInput} onChange={e => setTagInput(e.target.value)} disabled={loading} placeholder="Add tag" aria-label="Add tag" tabIndex={7} />
          <Button type="button" onClick={handleAddTag} disabled={loading || !tagInput} tabIndex={8}>Add</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {values.tags?.map(tag => (
            <span key={tag} className="bg-sauna-orange/10 text-sauna-orange px-2 py-1 rounded text-xs flex items-center gap-1">
              {tag}
              <button type="button" onClick={() => handleRemoveTag(tag)} className="ml-1 text-xs" aria-label={`Remove tag ${tag}`} tabIndex={9}>×</button>
            </span>
          ))}
        </div>
      </fieldset>
      {/* Submission */}
      <div className="pt-4">
        <Button type="submit" disabled={loading} aria-label="Save Blog Post" tabIndex={10}>{loading ? 'Saving...' : 'Save Blog Post'}</Button>
      </div>
    </form>
  );
};

export default BlogForm; 